import React, { useState, useEffect, useRef, FC } from "react";
import { Link } from "react-router-dom";
import Content from "../../layout/content/Content";
import { Card, Badge, Col, Row } from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  PreviewAltCard,
} from "../../components/Component";
import BudgetAside from "./BudgetAside";
import BudgetVsSpendAnalysis from "./analytics/BudgetVsSpendAnalysis";
import MaterialWiseSpendAnalysis from "./analytics/MaterialWiseSpendAnalysis";
import MoreAnalyticsOnBudget from "./analytics/MoreAnalyticsOnBudget";
import { Select, DatePicker, Flex, Spin, Empty } from "antd";
import moment, { Moment } from "moment";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import budgetApis from "../../api/master/budget";
import supplierApis from "../../api/master/supplier";
import Swal from "sweetalert2";
import contractorsApis from "../../api/master/contractors";

const { Option } = Select;
const { RangePicker } = DatePicker;

const BudgetAnalytics: FC = () => {
  const {
    data: currentUser,
    loading,
    error,
  } = useSelector((state: RootState) => state.currentUser);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const [site, setSite] = useState("");
  const [siteOptions, setSiteOptions] = useState<any>([]);

  const [category, setCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<any>([]);

  const [contractor, setContractor] = useState("");
  const [contractorOptions, setContractorOptions] = useState<any>([]);

  const [dateRange, setDateRange] = useState("Quarter to Date");
  const [customDateRange, setCustomDateRange] = useState<any>();

  const [budgetVsSpendChartData, setBudgetVsSpendChartData] = useState<any>(null);
  const [materialWiseSpendAnalysis, setMaterialWiseSpendAnalysis] =
    useState<any>([]);
  const [
    materialWiseBudgetOverrunbyCategory,
    setMaterialWiseBudgetOverrunbyCategory,
  ] = useState<any>([]);
  const [HeatMapwithSitewiseOverrun, setHeatMapwithSitewiseOverrun] =
    useState<any>([]);
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  // Tracks whether the contractor list for the CURRENT site has finished
  // loading. The contractor fetch is gated behind site, so on initial load
  // contractor settles later than site/category. Without this gate, a
  // 120ms debounce isn't long enough to bridge a slow contractor fetch
  // and we end up firing the analytics endpoints twice.
  const [contractorReady, setContractorReady] = useState(false);
  // Monotonic request id — only the latest in-flight load may apply its
  // results / flip loading state. Prevents stale responses from racing.
  const loadRequestIdRef = useRef(0);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility && siteAccessibility.length > 0) {
        const allSites: any = [];
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          allSites.push(item.siteID);
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSite(allSites.join(","));
        setSiteOptions([
          {
            label: "All Sites",
            value: allSites?.join(","),
          },
          ..._siteOptions,
        ]);
      } else {
        // No sites mapped — stop the spinner so the empty state can render
        setSite("");
        setSiteOptions([]);
        setHasEverLoaded(true);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, status } = await supplierApis.fetchSupplierCategory();
      if (status) {
        const allCategory: any = [];
        const _categoryOptions: any = [];
        data?.forEach((item: any) => {
          console.log("data", item);
          allCategory.push(item.id);
          _categoryOptions.push({ label: item.category, value: item.id });
        });
        setCategory(allCategory.join(","));
        setCategoryOptions([
          {
            label: "All Categories",
            value: allCategory?.join(","),
          },
          ..._categoryOptions,
        ]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContractorList = async (siteId: any) => {
    try {
      const { data, status } = await contractorsApis.fetchContractorBySiteId({
        id: siteId + "",
      });
      if (status) {
        let allContractors: any = [];
        let _contractorOptions: any = [];
        data.forEach((item: any) => {
          allContractors.push(item?.contractorID?.toString());
          _contractorOptions.push({
            label: item?.companyName,
            value: item?.contractorID?.toString(),
          });
        });

        setContractor(allContractors?.join(","));
        if (_contractorOptions.length > 1) {
          _contractorOptions = [
            {
              label: "ALL",
              value: allContractors?.join(","),
            },
            ..._contractorOptions,
          ];
        }
        setContractorOptions(_contractorOptions);
      }
    } catch (error) {
      console.log(error);
    } finally {
      // Mark contractor list as ready regardless of success/failure — the
      // analytics endpoints use `contractor || null`, so an empty list is
      // a valid input. Without this, a contractor fetch failure would
      // permanently block the analytics endpoints from firing.
      setContractorReady(true);
    }
  };


  const convertToDates = (dateType: string) => {
    switch (dateType) {
      case "Month to Date":
        return {
          from: dayjs().startOf("month").format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      case "Quarter to Date":
        return {
          from: dayjs()
            .subtract(3, "month")
            .startOf("month")
            .format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      case "Year to Date":
        return {
          from: dayjs().startOf("year").format("YYYY-MM-DD"),
          to: dayjs().format("YYYY-MM-DD"),
        };
      default:
        return {
          from: dayjs(customDateRange[0]).format("YYYY-MM-DD"),
          to: dayjs(customDateRange[1]).format("YYYY-MM-DD"),
        };
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const handleSiteChange = (value: string) => {
    setSite(value.toString());
  };

  const fetchBudgetvsSpendAnalysis = async (formData: any, requestId: number) => {
    try {
      const res = await budgetApis.budgetSpendAnalysis(formData);
      if (loadRequestIdRef.current !== requestId) return;
      const { response, status, message } = res;
      if (status) {
        setBudgetVsSpendChartData(response);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterialWiseSpendAnalysis = async (formData: any, requestId: number) => {
    try {
      const res = await budgetApis.materialWiseSpendAnalysis(formData);
      if (loadRequestIdRef.current !== requestId) return;
      const { response, status } = res;
      if (status) {
        setMaterialWiseSpendAnalysis(response.materialWiseList);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchMaterialWiseBudgetOverRun = async (formData: any, requestId: number) => {
    try {
      const res = await budgetApis.materialWiseBudgetOverRun(formData);
      if (loadRequestIdRef.current !== requestId) return;
      const { response, status } = res;
      if (status) {
        setMaterialWiseBudgetOverrunbyCategory(response);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    if (value !== "Custom Date") {
      setCustomDateRange(null);
    }
  };

  const handleCustomDateChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setCustomDateRange([
        moment(dates[0].toDate()),
        moment(dates[1].toDate()),
      ]);
    } else {
      setCustomDateRange(null);
    }
  };

  const handleCategoryChange = (value: string) => {
    setCategory(value.toString());
  };

  useEffect(() => {
    // Filters are populated asynchronously (currentUser → site,
    // fetchCategories → category, fetchContractorList → contractor). The
    // contractor fetch resolves last and may exceed the debounce window,
    // so we ALSO gate on `contractorReady` to ensure the analytics
    // endpoints fire exactly once per settled filter state.
    if (!site || !category || !contractorReady) return;

    const timer = setTimeout(() => {
      const requestId = ++loadRequestIdRef.current;
      const { from, to } = convertToDates(dateRange);
      const formData = {
        siteId: site,
        contractorId: contractor || null,
        categoryId: category,
        startDate: from,
        endDate: to,
      };

      setIsFiltersLoading(true);
      Promise.all([
        fetchMaterialWiseSpendAnalysis(formData, requestId),
        fetchMaterialWiseBudgetOverRun(formData, requestId),
        fetchBudgetvsSpendAnalysis(formData, requestId),
      ]).finally(() => {
        // Only the latest request flips loading off — older racing
        // requests have been superseded by a newer filter change.
        if (loadRequestIdRef.current !== requestId) return;
        setIsFiltersLoading(false);
        setHasEverLoaded(true);
      });
    }, 120);

    return () => clearTimeout(timer);
  }, [site, contractor, category, dateRange, customDateRange, contractorReady]);


  useEffect(() => {
    if (site) {
      // Reset readiness BEFORE the new fetch so the loadAll effect doesn't
      // race ahead with stale contractor state when site changes.
      setContractorReady(false);
      fetchContractorList(site);
    }
  }, [site]);

  return (
    <>
      <Head title="Budget Analytics" />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
                sm ? "content-active" : ""
              }`}
            >
              <BudgetAside updateSm={setSm} sm={sm} />
            </div>
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">Budget Analytics</BlockTitle>
                    <BlockDes>
                      <p>
                        Integrated Dashboard for Budget and Expense Analysis
                      </p>
                    </BlockDes>
                  </BlockHeadContent>
                </BlockBetween>
                <div className="d-flex align-items-center flex-wrap">
                  <div style={{ marginRight: 16, marginTop: 8 }}>
                    <label style={{ fontWeight: "bold", marginRight: 8 }}>
                      Filter by Site:
                    </label>
                    <Select
                      defaultValue="ALL"
                      style={{ width: 150 }}
                      onChange={handleSiteChange}
                      disabled={isFiltersLoading}
                    >
                      {siteOptions?.map((item: any) => (
                        <Option value={item.value}>{item.label}</Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginRight: 16, marginTop: 8 }}>
                    <label style={{ fontWeight: "bold", marginRight: 8 }}>
                      Filter by Contractor:
                    </label>
                    <Select
                      defaultValue={
                        contractorOptions?.length == 1
                          ? contractorOptions[0].label
                          : "ALL"
                      }
                      style={{ width: 150 }}
                      onChange={(value: any) => {
                        setContractor(value);
                      }}
                      disabled={isFiltersLoading}
                    >
                      {contractorOptions?.map((item: any) => (
                        <Option value={item.value}>{item.label}</Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginRight: 16, marginTop: 8 }}>
                    <label style={{ fontWeight: "bold", marginRight: 8 }}>
                      Filter by Category:
                    </label>
                    <Select
                      defaultValue="ALL"
                      style={{ width: 150 }}
                      onChange={handleCategoryChange}
                      disabled={isFiltersLoading}
                    >
                      {categoryOptions?.map((item: any) => (
                        <Option value={item.value}>{item.label}</Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontWeight: "bold", marginRight: 8 }}>
                      Filter by Date Range:
                    </label>
                    <Select
                      defaultValue="Quarter to Date"
                      style={{ width: 200, marginRight: 16 }}
                      onChange={handleDateRangeChange}
                      disabled={isFiltersLoading}
                    >
                      <Option value="Month to Date">Month to Date</Option>
                      <Option value="Quarter to Date">Quarter to Date</Option>
                      <Option value="Year to Date">Year to Date</Option>
                      <Option value="Custom Date">Custom Date</Option>
                    </Select>
                    {dateRange === "Custom Date" && (
                      <RangePicker
                        value={
                          customDateRange
                            ? [
                                dayjs(customDateRange[0].toDate()),
                                dayjs(customDateRange[1].toDate()),
                              ]
                            : null
                        }
                        onChange={(dates, dateStrings) =>
                          handleCustomDateChange(dates)
                        }
                        style={{ width: 300 }}
                        disabled={isFiltersLoading}
                      />
                    )}
                  </div>
                </div>
              </BlockHead>
              {!hasEverLoaded ? (
                <>
                  <Flex align="center" gap="middle" className="pageLoader">
                    <Spin size="large" className="m-auto" />
                  </Flex>
                </>
              ) : !site ? (
                <Block>
                  <Empty
                    description="No sites are mapped to your account yet. Please contact your administrator to configure site access."
                    style={{ padding: "60px 0" }}
                  />
                </Block>
              ) : (
                <div style={{ position: "relative", marginTop: 24 }}>
                  {isFiltersLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Spin size="large" tip="Loading analytics data..." />
                    </div>
                  )}
                  <Block>
                    <Row className="g-gs">
                      <Col lg="12">
                        <PreviewAltCard
                          className="card-full"
                          bodyClass={undefined}
                        >
                          <BudgetVsSpendAnalysis
                            budgetVsSpendData={budgetVsSpendChartData}
                          />
                        </PreviewAltCard>
                      </Col>
                    </Row>
                    <Row className="g-gs mt-1">
                      <Col lg="8">
                        <PreviewAltCard
                          className="card-full"
                          bodyClass={undefined}
                        >
                          <MaterialWiseSpendAnalysis
                            materialWiseSpendAnalysisData={
                              materialWiseSpendAnalysis
                            }
                          ></MaterialWiseSpendAnalysis>
                        </PreviewAltCard>
                      </Col>
                      <Col lg="4">
                        {materialWiseBudgetOverrunbyCategory && (
                          <MoreAnalyticsOnBudget
                            materialWiseBudgetOverrunbyCategoryData={
                              materialWiseBudgetOverrunbyCategory
                            }
                          ></MoreAnalyticsOnBudget>
                        )}
                      </Col>
                    </Row>
                  </Block>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default BudgetAnalytics;
